import { useState, useCallback, useContext, useEffect } from 'react';
import IGetPOShipmentDTO from '../../models/DTO/IGetPOShipmentDTO';
import IPO_Shipment from '../../models/IPO_Shipment';
import IPO_Shipment_Catalog_Item_MapUI from '../../models/IPO_Shipment_Catalog_Item_MapUI';
import EndpointsEnum from '../../models/EndpointsEnum';
import UserContext from '@contexts/UserContext';
import { SocketContext } from '@contexts/SocketContext';
import SupplierContext from '@contexts/SupplierContext';
import IWarehouse from '@models/IWarehouse';
import IGetWarehousesDTO from '@models/DTO/IGetWarehousesDTO';
import IOnGetWarehousesDTO from '@models/DTO/IOnGetWarehousesDTO';
import IUiSocketSetup from '@models/IUiSocketSetup';
import IOnGetPOShipmentDTO from '@models/DTO/IOnGetPOShipmentDTO';

interface UsePurchaseOrderShipmentDataProps {
    poShipmentGuid: string;
}

export const usePurchaseOrderShipmentData = ({ 
    poShipmentGuid 
}: UsePurchaseOrderShipmentDataProps) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [poShipment, setPoShipment] = useState<IPO_Shipment>();
    const [poShipmentItems, setPoShipmentItems] = useState<IPO_Shipment_Catalog_Item_MapUI[]>();
    const [dataFetch, setDataFetch] = useState(false);
    const [initialData, setInitialData] = useState(true);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [warehouses, setWarehouses] = useState<IWarehouse[]>([]);

    const getPOShipment = useCallback(() => {
        const payload: IGetPOShipmentDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            poShipmentGuid,
        };

        socket.emit(EndpointsEnum.PurchaseOrderGetPOShipment, payload);
        setDataFetch(true);
    }, [userModel.guid, userModel.token, supplierModel.guid, poShipmentGuid, socket]);

    const getWarehouses = useCallback(() => {
        const payload: IGetWarehousesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.WarehouseGetWarehouses, payload);
    }, [userModel.guid, userModel.token, supplierModel.guid, socket]);

    const onGetPOShipment = (data: IOnGetPOShipmentDTO) => {
        setPoShipment(data.poShipment);
        const poShipmentItems: IPO_Shipment_Catalog_Item_MapUI[] = data.poShipment!.PO_Shipment_Catalog_Item_Maps!.map((itemMap) => {
            return {
                ...itemMap,
                isDirty: false,
            };
        });
        setPoShipmentItems(poShipmentItems);
        setDataFetch(false);
        setInitialData(false);
    }

    const onGetWarehouses = (data: IOnGetWarehousesDTO) => {
        setWarehouses(data.warehouses);
    }

    const sockets: IUiSocketSetup[] = [
        {endpoint: EndpointsEnum.PurchaseOrderOnGetPOShipment, fn: onGetPOShipment},
        {endpoint: EndpointsEnum.WarehouseOnGetWarehouses, fn: onGetWarehouses},
    ]

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        getPOShipment();
        getWarehouses();

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    return {
        poShipment,
        setPoShipment,
        poShipmentItems,
        dataFetch,
        warehouses,
        setPoShipmentItems,
        setDataFetch,
        initialData,
        setInitialData,
        isFormDirty,
        setIsFormDirty,
        getPOShipment
    };
}; 